// Amplify v6 (Gen 2 SDK) data-layer client.
//
// Uses the lazy, memoized, schema-typed `getClient()` from
// `./amplify-client` (typed `<Schema>` as of issue #146). The v6 idiom is
// `getClient().models.ProdApplicant.list({ ... })`.
import { getClient } from "./amplify-client";
import { list } from "aws-amplify/storage";
import React, { createContext, useReducer, ReactNode } from "react";
import { ApplicantInfo, filesToBeUploaded } from "./types";
import { naWrapper } from "./utils";

const getDefaultContextFilter = () =>
  (localStorage?.getItem("ContextFilter") as ContextFilter) ?? "all";

export type ContextFilter = "all" | "complete" | "screening";

export const contextFilterLabels: { [key in ContextFilter]: string } = {
  all: "全出願者",
  complete: "出願完了者",
  screening: "書類選考合格者",
};

interface DataContextState {
  loading: boolean;
  rawData: ApplicantInfo[];
  data: ApplicantInfo[];
  filter: ContextFilter;
}

export const initialState: DataContextState = {
  loading: true,
  rawData: [],
  data: [],
  filter: getDefaultContextFilter(),
};

export const DataContext = createContext<{
  state: DataContextState;
  dispatch?: React.Dispatch<Action>;
}>({
  state: initialState,
});

export enum ActionType {
  GET_DATA = "GET_DATA",
  GET_DATA_COMPLETE = "GET_DATA_COMPLETE",
  SET_FILTER = "SET_FILTER",
}
type GetDataAction = {
  type: ActionType.GET_DATA;
};
type GetDataCompleteAction = {
  type: ActionType.GET_DATA_COMPLETE;
  payload: ApplicantInfo[];
};
type SetFilterAction = {
  type: ActionType.SET_FILTER;
  payload: ContextFilter;
};
type Action = GetDataAction | GetDataCompleteAction | SetFilterAction;

export const dataReducer = (state: DataContextState, action: Action) => {
  switch (action.type) {
    case ActionType.GET_DATA:
      return {
        ...state,
        loading: true,
        rawData: [],
        data: [],
      };
    case ActionType.GET_DATA_COMPLETE:
      return {
        ...state,
        loading: false,
        rawData: action.payload,
        data: filteredData(action.payload, state.filter),
      };
    case ActionType.SET_FILTER:
      const { rawData } = state;
      return {
        ...state,
        filter: action.payload,
        data: filteredData(rawData, action.payload),
      };
    default:
      return state;
  }
};

// The data client is constructed lazily at use-time via `getClient()`
// (see `amplify-client.ts`). It is NOT built at module scope: doing so
// would snapshot the Amplify config before `Amplify.configure(...)` ran
// whenever the bundler evaluates this module first, which is exactly the
// deployed-bundle `Amplify has not been configured` regression this fix
// removes. `getClient()` is invoked inside `fetchApplicantsList` below,
// which only runs after app bootstrap has configured Amplify. The shared
// `getClient()` is now typed (`generateClient<Schema>()`); this loader
// still re-widens to `any[]` locally for the downstream prefix-stripping
// logic -- consuming the typed row shapes here is out of scope for the
// TS-5 bump (see #146 / #148).

/**
 * v5 list keys returned without the `public/` prefix because the SDK
 * stripped it implicitly (`level: "public"`); v6 returns the literal path
 * the bucket sees, including the prefix. Strip it once here so the
 * downstream comparison
 *   `files.includes(`${item.applicantNum} ${item.name}/${type}.pdf`)`
 * keeps its v5 shape and existing per-applicant logic doesn't change.
 */
const stripPublicPrefix = (path: string): string =>
  path.startsWith("public/") ? path.slice("public/".length) : path;

// Defensive cap on the pagination loops below. The cycle today carries
// ~50-200 applicants and a similar count of recommender-uploaded files;
// 50 pages at the default page sizes (100 for AppSync data-client,
// 1000 for storage) yields headroom of two orders of magnitude. If a
// loader ever hits this cap, the user-visible result is a partial list
// (the same failure mode this commit was filed to prevent) but the
// browser tab won't lock up while accumulating an unbounded result --
// see review thread `45524e89...` on MR !42 for the precedent
// (`cognito-handler/list_recommender/app.py`, decision row 2026-05-17).
const MAX_LIST_PAGES = 50;

export const fetchApplicantsList = async (dispatch: React.Dispatch<Action>) => {
  dispatch({ type: ActionType.GET_DATA });

  // v6: typed list via `models.ProdApplicant.list`; no codegen string. The
  // Q3 (#143) coercion of `applicantNum` int->string happens at the page
  // level (sub-steps 5-6); this loader keeps the row shape Amplify returns
  // and downstream code that already tolerates the dynamic shape continues
  // to work as it did under v5's `as any` cast.
  //
  // Paginate via `nextToken` so the staff-facing applicant table never
  // silently truncates above the per-page cap. Issue #125 set the
  // precedent for the Recommender-table scan; same shape applies here.
  // Without this loop the prior `limit: 200` call would silently drop
  // applicant #201 from triage during crunch week.
  const items: any[] = [];
  let nextToken: string | null | undefined = undefined;
  for (let page = 0; page < MAX_LIST_PAGES; page += 1) {
    const result: { data: any[]; nextToken?: string | null } =
      await getClient().models.ProdApplicant.list({
        limit: 200,
        nextToken,
      });
    if (result.data) items.push(...result.data);
    nextToken = result.nextToken;
    if (!nextToken) break;
  }
  if (nextToken) {
    // Logged but non-fatal: see MAX_LIST_PAGES comment above.
    console.warn(
      `fetchApplicantsList: hit MAX_LIST_PAGES=${MAX_LIST_PAGES}; result may be truncated`,
    );
  }

  // v6: `list({ path })` returns `{ items: [{ path, eTag, ... }, ...] }`.
  // Pass the literal `public/` prefix (operator answer Q5 of #143) and
  // strip it back off so the downstream comparison is unchanged.
  //
  // Use `listAll: true` so the SDK handles pagination internally and
  // returns every object under the prefix. Without this option the SDK
  // defaults to `pageSize: 1000` and returns just the first page; the
  // per-applicant `files.includes(...)` lookup below depends on the
  // full set being present (otherwise an applicant whose files happen
  // to land on page 2 gets falsely flagged as missing recommendation
  // letters in the staff list). Recommender uploads are typically
  // <300 per cycle today; the cap-less full listing is the right shape
  // for this caller.
  const { items: storageItems } = await list({
    path: "public/",
    options: { listAll: true },
  });
  const files = storageItems.map((item) => stripPublicPrefix(item.path));

  const applicants = items.map((item: any) => {
    // Check if tags are null or undefined
    var newItem = item;
    if (newItem.tags == null) {
      newItem.tags = [];
    }
    const missingFiles: (typeof filesToBeUploaded)[number][] = [];
    filesToBeUploaded.forEach((type) => {
      if (files.includes(`${item.applicantNum} ${item.name}/${type}.pdf`))
        return;
      else missingFiles.push(type);
    });
    newItem.missingFiles = missingFiles;

    // Utilize the missingFiles logic and put missingFiles info into individual attributes in ApplicantInfo
    // They don't come in order so you have to check if each one is included in the missingFiles array.
    // TODO: [2024 Update] Reversed the logic here and now it is tracking submitted files. This is a temporary fix.
    // Refer to issue #84 for more details.
    newItem.recommendation1 = missingFiles.includes("推薦状１")
      ? null
      : "推薦状１"; // Swapped this logic here
    newItem.recommendation2 = missingFiles.includes("推薦状２")
      ? null
      : "推薦状２";
    newItem.recommendation3 = missingFiles.includes("留学中の調査書")
      ? null
      : "留学中の調査書";
    newItem.schoolReport = missingFiles.includes("調査書") ? null : "調査書";

    return newItem;
  });

  dispatch({
    type: ActionType.GET_DATA_COMPLETE,
    payload: applicants,
  });
};

const filteredData = (rawData: ApplicantInfo[], filter: ContextFilter) => {
  if (filter === "complete")
    return rawData.filter(({ isComplete }) => naWrapper(isComplete).includes("領収"));
  if (filter === "screening")
    return rawData.filter(
      ({ result1 }) =>
        result1 && !result1.includes("不合格") && result1.includes("合格")
    );
  return rawData;
};

const DataContextProvider: React.FC<{ children?: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(dataReducer, initialState);
  return (
    <DataContext.Provider value={{ state, dispatch }}>
      {children}
    </DataContext.Provider>
  );
};

export default DataContextProvider;
